import { PATHNAMES, SOCIAL_MEDIA_BASE_URLS } from '@/constants/urls'

import { Code as CountryCode } from '@/types'

// Organization Schema Markup for Butternut Box
interface OrganizationSchema {
  '@context': string
  '@type': string
  name: string
  url: string
  image: string
  logo: string
  description: string
  sameAs: string[]
  foundingDate: string
  contactPoint: {
    '@type': string
    url: string
    contactType: string
    availableLanguage: string[]
  }
  address: {
    '@type': string
    streetAddress: string
    addressLocality: string
    postalCode: string
    addressCountry: string
  }
  areaServed: {
    '@type': string
    name: string
  }
}

interface CountryConfig {
  name?: string
  url?: string
  image?: string
  logo?: string
  description: string
  sameAs: string[]
  foundingDate?: string
  contactPoint?: {
    url?: string
    availableLanguage: string[]
  }
  address?: {
    streetAddress: string
    addressLocality: string
    postalCode: string
    addressCountry: string
  }
  areaServed: string
}

const createButternutConfig = (
  overrides: Partial<CountryConfig>
): CountryConfig => ({
  description:
    'Butternut Box is a fresh dog food subscription. Our meals are healthy, delicious and hypoallergenic. Perfectly-portioned and delivered direct to your door.',
  sameAs: [
    `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.GB.FACEBOOK}`,
    `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.GB.INSTAGRAM}/`,
    `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.GB.TIK_TOK}`,
    'https://www.youtube.com/channel/UCs3jN8UsEUF3unNI-IBXhgw',
    'https://uk.trustpilot.com/review/butternutbox.com'
  ],
  areaServed: CountryCode.GB,
  ...overrides
})

const createPsiBufetConfig = (
  overrides: Partial<CountryConfig>
): CountryConfig => ({
  name: 'PsiBufet',
  foundingDate: '2012-01-01',
  description:
    'PsiBufet to specjalista, jeśli chodzi o świeżo mrożone jedzenie dla psów. Wszystkie zdrowe posiłki ze świeżo przygotowanych składników dostarczone prosto do Twoich drzwi. Przyrządzone ze składników jakości human-grade, idealne dla niejadków lub wrażliwych brzuszków.',
  sameAs: [
    `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.PL.FACEBOOK}/`,
    `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.PL.INSTAGRAM}/`,
    `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.PL.TIK_TOK}`,
    'https://x.com/PsiBufet',
    'https://www.youtube.com/channel/UCpgmlfPNZJlSv0i_R8VokVA'
  ],
  areaServed: CountryCode.PL,
  ...overrides
})

const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  [CountryCode.GB]: createButternutConfig({}),
  [CountryCode.NI]: createButternutConfig({}),
  [CountryCode.IE]: createButternutConfig({
    description:
      'Butternut Box makes fresh, human-quality dog food tailored to their specific needs. Healthy and delivered straight to your door.',
    sameAs: [
      `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.IE.FACEBOOK}`,
      `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.IE.INSTAGRAM}/`,
      `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.IE.TIK_TOK}`,
      'https://www.youtube.com/channel/UCs3jN8UsEUF3unNI-IBXhgw',
      'https://ie.trustpilot.com/review/butternutbox.com'
    ],
    areaServed: CountryCode.IE
  }),
  [CountryCode.DE]: createButternutConfig({
    description:
      'Butternut Box ist ein Frischfutter-Abo für Hunde. Unsere Mahlzeiten sind gesund, lecker und hypoallergen. Sie sind perfekt portioniert und werden direkt zu dir nach Hause geliefert.',
    sameAs: [
      `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.DE.FACEBOOK}`,
      `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.DE.INSTAGRAM}/`,
      `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.DE.TIK_TOK}`,
      'https://x.com/butternutbox',
      'https://www.youtube.com/channel/UCs3jN8UsEUF3unNI-IBXhgw',
      'https://de.trustpilot.com/review/butternutbox.com',
      'https://www.trustedshops.de/bewertung/info_X8D1D295071F2A502117BCCDFBCFBEC54.html'
    ],
    contactPoint: {
      url: 'https://butternutbox.com/contact',
      availableLanguage: ['de', 'en']
    },
    address: {
      streetAddress: 'Münzstraße 12',
      addressLocality: 'Berlin',
      postalCode: '10178',
      addressCountry: 'DE'
    },
    areaServed: CountryCode.DE
  }),
  [CountryCode.NL]: createButternutConfig({
    description:
      'Butternut Box is dé plek om verse maaltijden voor je hond te bestellen. We bezorgen de voedzame en versbereide maaltijden thuis wanneer het jou uitkomt. Gemaakt met hoge kwaliteit ingrediënten en perfect voor kieskeurige eters of gevoelige maagjes.',
    sameAs: [
      `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.NL.FACEBOOK}/`,
      `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.NL.INSTAGRAM}/`,
      `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.NL.TIK_TOK}`,
      'https://x.com/butternutbox',
      'https://www.youtube.com/channel/UCs3jN8UsEUF3unNI-IBXhgw',
      'https://nl.trustpilot.com/review/butternutbox.com'
    ],
    contactPoint: {
      url: 'https://butternutbox.com/contact',
      availableLanguage: ['nl', 'en']
    },
    address: {
      streetAddress: 'Weteringschans 165 C',
      addressLocality: 'Amsterdam',
      postalCode: '1017 XD',
      addressCountry: 'NL'
    },
    areaServed: CountryCode.NL
  }),
  [CountryCode.BE]: createButternutConfig({
    description:
      'Butternut Box is dé plek om verse maaltijden voor je hond te bestellen. We bezorgen de voedzame en versbereide maaltijden thuis wanneer het jou uitkomt. Gemaakt met hoge kwaliteit ingrediënten en perfect voor kieskeurige eters of gevoelige maagjes.',
    sameAs: [
      `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.BE.FACEBOOK}/`,
      `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.BE.INSTAGRAM}/`,
      `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.BE.TIK_TOK}`,
      'https://x.com/butternutbox',
      'https://www.youtube.com/channel/UCs3jN8UsEUF3unNI-IBXhgw',
      'https://nl-be.trustpilot.com/review/butternutbox.com'
    ],
    contactPoint: {
      url: 'https://butternutbox.com/contact',
      availableLanguage: ['nl', 'en', 'fr']
    },
    address: {
      streetAddress: 'Weteringschans 165 C',
      addressLocality: 'Amsterdam',
      postalCode: '1017 XD',
      addressCountry: 'NL'
    },
    areaServed: CountryCode.BE
  }),
  [CountryCode.PL]: createPsiBufetConfig({
    url: 'https://psibufet.pl/',
    image:
      'https://cdn.butternutbox.com/asset_management/assets/psibufet-square-logo/pl_PL',
    logo: 'https://cdn.butternutbox.com/asset_management/assets/psibufet-square-logo/pl_PL',
    description:
      'PsiBufet to specjalista, jeśli chodzi o świeżo mrożone jedzenie dla psów. Wszystkie zdrowe posiłki ze świeżo przygotowanych składników dostarczone prosto do Twoich drzwi. Przyrządzone ze składników jakości human-grade, idealne dla niejadków lub wrażliwych brzuszków.',
    sameAs: [
      `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.PL.FACEBOOK}/`,
      `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.PL.INSTAGRAM}/`,
      `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.PL.TIK_TOK}`,
      'https://x.com/PsiBufet',
      'https://www.youtube.com/channel/UCpgmlfPNZJlSv0i_R8VokVA',
      'https://pl.trustpilot.com/review/psibufet.pl'
    ],
    contactPoint: {
      url: 'https://psibufet.pl/pomoc',
      availableLanguage: ['pl']
    },
    address: {
      streetAddress: 'Wólczyńska 133',
      addressLocality: 'Warszawa',
      postalCode: '01-919',
      addressCountry: 'PL'
    },
    areaServed: CountryCode.PL
  }),
  [CountryCode.CZ]: createPsiBufetConfig({
    url: 'https://psibufet.cz/',
    image:
      'https://cdn.butternutbox.com/asset_management/assets/psibufet-square-logo/pl_PL',
    logo: 'https://cdn.butternutbox.com/asset_management/assets/psibufet-square-logo/pl_PL',
    description:
      'PsiBufet nabízí předplatné čerstvého krmiva pro psy. Naše krmiva jsou zdravá, chutná a hypoalergenní. Dokonalé porce, dodávané až k\u00A0vašim dveřím.',
    sameAs: [
      `${SOCIAL_MEDIA_BASE_URLS.FACEBOOK}/${PATHNAMES.CZ.FACEBOOK}/`,
      `${SOCIAL_MEDIA_BASE_URLS.INSTAGRAM}/${PATHNAMES.CZ.INSTAGRAM}/`,
      `${SOCIAL_MEDIA_BASE_URLS.TIK_TOK}/${PATHNAMES.CZ.TIK_TOK}`,
      'https://x.com/PsiBufet',
      'https://www.youtube.com/channel/UCpgmlfPNZJlSv0i_R8VokVA',
      'https://pl.trustpilot.com/review/psibufet.pl'
    ],
    contactPoint: {
      url: 'https://psibufet.cz/pomoc',
      availableLanguage: ['cs']
    },
    address: {
      streetAddress: 'Wólczyńska 133',
      addressLocality: 'Warszawa',
      postalCode: '01-919',
      addressCountry: 'PL'
    },
    areaServed: CountryCode.CZ
  })
}

const getOrganizationSchema = (
  shippingCountryCode: string
): OrganizationSchema => {
  const config =
    COUNTRY_CONFIGS[shippingCountryCode] || COUNTRY_CONFIGS[CountryCode.GB]

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: config.name || 'Butternut Box',
    url: config.url || 'https://www.butternutbox.com',
    image:
      config.image ||
      'https://cdn.butternutbox.com/asset_management/assets/butternut-box-footer-logo ',
    logo:
      config.logo ||
      'https://cdn.butternutbox.com/asset_management/assets/butternut-box-footer-logo ',
    description: config.description,
    sameAs: config.sameAs,
    foundingDate: config.foundingDate || '2016-03-13',
    contactPoint: {
      '@type': 'ContactPoint',
      url: config.contactPoint?.url || 'https://butternutbox.com/help',
      contactType: 'customer support',
      availableLanguage: config.contactPoint?.availableLanguage || ['en']
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress:
        config.address?.streetAddress || 'Gateway Central, 187 Wood Ln',
      addressLocality: config.address?.addressLocality || 'London',
      postalCode: config.address?.postalCode || 'W12 7SA',
      addressCountry: config.address?.addressCountry || CountryCode.GB
    },
    areaServed: {
      '@type': 'Country',
      name: config.areaServed
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const homeContentTag = document.getElementById('home-content-tag')
  const shippingCountryCode =
    homeContentTag?.dataset.shippingCountryCode || CountryCode.GB

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(
    getOrganizationSchema(shippingCountryCode)
  )
  document.head.appendChild(script)
  console.log(getOrganizationSchema(shippingCountryCode))
})
